<template>
	<div v-html="formatString" class="link-document-markdown" :class="[`link-document-markdown-type-${type}`]"></div>
</template>

<script>
import MarkdownItTableOfContents from './markdown-it-table-of-contents.js'
import markdownItTextualUml from 'markdown-it-textual-uml'

export default {
	name: "link-document-markdown",
	props: {
		value: {type: String},
		type: {type: String, default: 'normal'},
	},
	data() {
		return {
			md: null,
		}
	},
	computed: {
		formatString() {
			return !this.md ? null : this.md.render(this.value || '');
		}
	},
	async mounted() {
		const [hljs, markdownit] = await Promise.all([this.$lv.resource.loadLib('hljs'), this.$lv.resource.loadLib('markdownit')])

		await Promise.all([
			'languages/java.min.js',
			'languages/json.min.js',
			'languages/markdown.min.js',
			'languages/scss.min.js',
			'languages/typescript.min.js',
		].map(item => this.$lv.resource.loadLibPart('hljs', item)))

		const md = markdownit({
			html: true,
			linkify: true,
			typography: true,
			highlight: function (str, lang) {
				if (lang && hljs.getLanguage(lang)) {
					try {
						return `<pre class="hljs language-${lang}"><code>${hljs.highlight(lang, str, true).value}</code></pre>`
					} catch (__) {
					}
				}
				return `<pre class="hljs"><code> ${md.utils.escapeHtml(str)} </code></pre>`;
			}
		})
		md.use(MarkdownItTableOfContents, {
			includeLevel: [2, 3, 4],
			markerPattern: /^\[toc\]/im
		})
		md.use(markdownItTextualUml)

		this.md = md
	}
}
</script>

<style lang="scss">

$code-font: source-code-pro, Menlo, Monaco, Consolas, Courier New, monospace;


.link-document {
	.markdown-important {
		color: $color-error;
		font-size: 16px;
		font-weight: bold;
	}

	h1, h2, h3, h4, h5, h6 {
		font-weight: normal;
		color: #333;
		font-family: inherit;
		padding-bottom: 16px;
		margin: 0 0 16px;
	}

	h2, h3, h4, h5, h6 {
		padding-top: 16px;
	}

	h1 {
		font-size: 22px;
		border-bottom: solid 1px #eee;
	}

	h2 {
		font-size: 18px;
	}

	h3 {
		font-size: 16px;
	}

	h4 {
		font-size: 14px;
	}

	h5 {
		font-size: 12px;
	}

	h6 {
		font-size: 10px;
	}

	em {
		font-style: normal;
	}
}

.link-document-markdown {
	overflow: hidden;
	background-color: white;
	padding: 16px;

	.line-number, code, kbd {
		font-family: source-code-pro, Menlo, Monaco, Consolas, Courier New, monospace;
		font-size: 0.85em;
		line-height: 1.5em;
	}

	pre {
		font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif;

		line-height: 1.4;
		padding: 15px !important;
		margin: .85rem 0;
		border-radius: 6px;
		overflow: auto;
		font-size: 1rem;

		code {
			margin: 0;
			background-color: rgba(27, 31, 35, .05);
			border-radius: 3px;
			color: white;
			padding: 0;
		}
	}
}

@include themeify {
	$padding: 3px 16px;
	$border-radius: 4px;
	$margin: 36px;

	p, td, li {
		& > code {
			background-color: #f2f2f2;
			border: solid 1px #999;
			padding: 1px 6px;
			border-radius: 2px;
			margin: 0 2px;
		}
	}

	.link-document-markdown {

		blockquote {
			margin-left: 0;
			margin-right: 0;
			background-color: rgba(pl-var(color-primary), 0.1);
			padding: 9px 32px;
			font-size: 14px;
			color: #333;
			border-left: 3px solid pl-var(color-primary);
			line-height: 32px;
		}

		& > p {
			font-size: 14px;
			line-height: 24px;

			em {
				color: red;
			}
		}

		ul, ol {
			padding-left: 2.5em;

			li {
				font-size: 14px;
				line-height: 36px;

				pre {
					line-height: 21px;
				}
			}
		}
	}

	*[class*=language-] {
		position: relative;
	}

	*[class*=language-]:before {
		position: absolute;
		z-index: 3;
		top: .8em;
		right: 1em;
		font-size: .75rem;
		color: hsla(0, 0%, 100%, .4);
		font-family: $code-font;
	}

	$lang: (
		javascript:'javascript',
		typescript:'typescript',
		js:'javascript',
		html:'html',
		json:'json',
		css:'css',
		scss:'scss',
		less:'less',
		sass:'sass',
		java:'java',
		script:'script',
		markdown:'markdown'
	);

	@each $key, $value in $lang {
		*[class~=language-#{$key}]:before {
			content: $value;
		}
	}


	.link-document-item-table table, .link-document-markdown > table {
		width: 100%;
		border-spacing: 0;
		border-collapse: collapse;
		margin-bottom: 32px;
		color: #333;

		td, th {
			font-size: 12px;
			border: solid 1px #DEE3E8;
		}

		thead {
			background-color: #FBFCFF;

			th {
				height: 32px;
				padding: 0 12px;
			}
		}

		tbody {
			td {
				height: 40px;
				padding: 0 12px;
				border-bottom: solid 1px #DEE3E8;
			}
		}
	}
}
</style>
